import React, { useEffect, useState, useMemo } from "react";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// REDUX
import { useSelector } from "react-redux";
// CUSTOM HOOKS
import usePostCommand from "../../../customHooks/POST_requests/usePostCommand";
import useGetAllMessages from "../../../customHooks/GET_requests/useGetAllMessages";
// COMPONENTS
import ActionCommandButton from "../Actions/components/ActionCommandButton";
import GlobalAlert2 from "../../home/GlobalAlert2";
// INTERFACES
import { RootState } from "../../../app/store";
import { ActionCommandOption } from "../../../interfaces/Bank.interface";
// CONSTANTS
import {
  resolveCommandEnum,
  resolveValidStateEnums,
  assertBoundCommand,
  UNBOUND_COMMAND_REASON,
  dispatchFloor,
  floorSelectionMissing,
} from "./actionButtonUtils";
import useAwaitingTargetReason from "./useAwaitingTargetReason";
import { LiftType } from "../../../flatbuffers/generated/archive/lift-type";
// VERIFICATION
import useActionOutcome, { ActionVerification } from "./useActionOutcome";

function ActionButtonToggleTimer({
  validStates,
  toggleValidStates,
  label,
  toggleLabel,
  messageId,
  toggleMessageId,
  command = null,
  toggleCommand = null,
  colorAction,
  colorToggleAction,
  timerAction,
  timerToggleAction,
  carService,
  targetFloor = undefined,
  expectedResponse = null,
  expectedResponseTimeout = null,
  commandMessageId = null,
}: {
  validStates: string[];
  toggleValidStates: string[];
  label: string;
  toggleLabel: string;
  messageId: string;
  toggleMessageId: string;
  command?: ActionCommandOption | null;
  toggleCommand?: ActionCommandOption | null;
  colorAction: string;
  colorToggleAction: string;
  timerAction: number;
  timerToggleAction: number;
  carService: number;
  /**
   * The floor this row's commands dispatch to. `undefined` for a command that
   * takes no floor (dispatches at 0, as before); `null` while a floor-taking
   * command is still waiting for the operator to pick one. See dispatchFloor.
   */
  targetFloor?: number | null;
} & ActionVerification) {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const [t] = useTranslation("global");

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [loadingCounter, setLoadingCounter] = useState<boolean>(false);
  const [counter, setCounter] = useState(0);
  const [localCarService, setLocalCarService] = useState<number>(0);

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { theme } = useSelector((state: RootState) => state.home);

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */

  // ************************************************ */
  // CUSTOM HOOKS *********************************** */
  const { triggerPostCommand, isLoadingPostCommand, isErrorPostCommand } = usePostCommand();
  const { messages } = useGetAllMessages();
  const reportOutcome = useActionOutcome({
    expectedResponse,
    expectedResponseTimeout,
    commandMessageId,
  });

  // ************************************************ */
  // MEMOIZED VALUES ******************************** */
  // null (not 0) when the message is unbound — enum 0 is a real Control
  // (CarCallLock), so a 0 fallback would mis-fire that command.
  const messageEnum = useMemo(
    () => resolveCommandEnum(messages, messageId, command),
    [messages, messageId, command],
  );

  const toggleMessageEnum = useMemo(
    () => resolveCommandEnum(messages, toggleMessageId, toggleCommand),
    [messages, toggleMessageId, toggleCommand],
  );

  const validStatesNumbers = useMemo(
    () => resolveValidStateEnums(messages, validStates),
    [messages, validStates],
  );

  const toggleValidStatesNumbers = useMemo(
    () => resolveValidStateEnums(messages, toggleValidStates),
    [messages, toggleValidStates],
  );

  // ************************************************ */
  // FUNCTIONS ************************************** */
  // Send the action's BOUND command, never a hardcoded Control value.
  // The cooldown is passed in rather than derived from a `lastCommandSent`
  // state variable: this now runs in the same tick as the click, where such a
  // variable would still hold the PREVIOUS press's value and pick the wrong
  // timer. Tracking which button was pressed is no longer needed at all.
  const launchTimed = (actionTimer: number) => {
    setLoadingCounter(true);
    let count = actionTimer;
    setCounter(count);
    const countdownInterval = setInterval(() => {
      count -= 1;
      setCounter(count);
      if (count === 0) {
        clearInterval(countdownInterval);
        setLoadingCounter(false);
      }
    }, 1000); // Update counter every second
    setTimeout(() => {
      clearInterval(countdownInterval);
      setLoadingCounter(false);
    }, actionTimer * 1000); // Stop countdown and loading indicator after 5 seconds
  };

  // The cooldown starts when the command is DELIVERED, not when the emulator
  // mutation reports success: that flag is permanently false on the EDGE and
  // OFFLINE paths, so on every production install the repeat-command guard this
  // type exists for never engaged at all.
  const onClickToggleOn = async () => {
    if (!assertBoundCommand(messageEnum, label, theme)) {
      reportOutcome(label, false, messageId);
      return;
    }
    const delivered = await triggerPostCommand({
      floor: dispatchFloor(targetFloor),
      command: messageEnum,
      liftType: LiftType[LiftType.ELEVATOR] as "ELEVATOR",
      active: true,
    });
    if (delivered) launchTimed(timerAction);
    reportOutcome(label, delivered, messageId);
  };

  const onClickToggleOff = async () => {
    if (!assertBoundCommand(toggleMessageEnum, toggleLabel, theme)) {
      reportOutcome(toggleLabel, false, toggleMessageId ?? null);
      return;
    }
    const delivered = await triggerPostCommand({
      floor: dispatchFloor(targetFloor),
      command: toggleMessageEnum,
      liftType: LiftType[LiftType.ELEVATOR] as "ELEVATOR",
      active: true,
    });
    if (delivered) launchTimed(timerToggleAction);
    reportOutcome(toggleLabel, delivered, toggleMessageId ?? null);
  };

  // ************************************************ */
  // USE EFFECT ************************************* */
  useEffect(() => {
    setLocalCarService(carService);
  }, [carService]);

  // *************************************************************** */
  // COMPONENT - TOGGLE TIMER ************************************** */
  // Two independently gated commands plus a cooldown. `loadingCounter` blocks
  // both while the configured timer runs down — that repeat-command guard is
  // the pre-existing behaviour and is preserved; the countdown now reads out
  // inside the buttons instead of replacing their labels.
  const primaryBlocked = validStatesNumbers.includes(localCarService);
  const secondaryBlocked = toggleValidStatesNumbers.includes(localCarService);
  const unavailableReason = t(
    "monitoringActions.unavailableInCurrentState",
    "Unavailable in the current service mode",
  );

  // A floor-taking command must not dispatch until the operator picks a floor
  // on the row's strip; `targetFloor` is null for exactly as long as that is
  // true. The gate is VISIBLE — a disabled button naming its reason — rather
  // than a click that silently sends nothing, and rather than the position-0
  // dispatch it replaces, which aimed a positional command at a floor the
  // operator never chose.
  const awaitingFloor = floorSelectionMissing(targetFloor);
  const selectFloorReason = useAwaitingTargetReason(messageEnum);
  // A function rather than a ternary at each call site: the floor gate outranks
  // the service-state gate, and both have to reach one tooltip slot without
  // nesting a conditional inside a conditional.
  const reasonFor = (stateBlocked: boolean, commandEnum: number | null): string | undefined => {
    // Ahead of the transient gates: a missing binding is a configuration
    // fault that no amount of waiting or floor-picking will clear, so it is
    // the reason worth showing.
    if (commandEnum == null) return UNBOUND_COMMAND_REASON;
    if (awaitingFloor) return selectFloorReason;
    if (stateBlocked) return unavailableReason;
    return undefined;
  };

  return (
    <>
      <ActionCommandButton
        label={label}
        countdown={loadingCounter ? counter : null}
        color={colorAction}
        disabled={
          primaryBlocked ||
          awaitingFloor ||
          isLoadingPostCommand ||
          loadingCounter ||
          messageEnum == null
        }
        loading={isLoadingPostCommand}
        disabledReason={reasonFor(primaryBlocked, messageEnum)}
        onClick={onClickToggleOn}
      />
      {/* Always rendered — a TOGGLETIMER has two commands by definition. A row
          saved without a paired command is misconfigured, and it must LOOK
          misconfigured: assertBoundCommand names it on click rather than the
          control quietly disappearing. */}
      <ActionCommandButton
        label={toggleLabel}
        countdown={loadingCounter ? counter : null}
        color={colorToggleAction}
        disabled={
          secondaryBlocked ||
          awaitingFloor ||
          isLoadingPostCommand ||
          loadingCounter ||
          toggleMessageEnum == null
        }
        loading={isLoadingPostCommand}
        disabledReason={reasonFor(secondaryBlocked, toggleMessageEnum)}
        onClick={onClickToggleOff}
      />
      {/** ---------------------------------------------------- */}
      {/** GLOBAL ALERT */}
      <GlobalAlert2
        isError={isErrorPostCommand}
        isSuccess={false}
        requestType="POST"
        error="Error"
        name="Command"
      />
    </>
  );
}

export default ActionButtonToggleTimer;
